/**
 *
 * Locations
 *
 */

import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EditOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Checkbox, Col, Row, Table, Typography } from 'antd';

import { Loading } from '../../components/Loading';

import { useQuery } from '@apollo/client';
import { GET_LOCATIONS_LIST } from '../../graphql/queries';

import EditLocationModal from './EditLocationModal';
import NewLocationModal from './NewLocationModal';
import SubLocationsModal from './SubLocationsModal';

const { Title } = Typography;

interface Props {}

export const Locations = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  const [newLocationModal, setNewLocationModal] = useState(false);
  const [editLocationModal, setEditLocationModal] = useState(false);
  const [subLocationsModal, setSubLocationsModal] = useState(false);

  const [newLocationModalKey, setNewLocationModalKey] = useState(0);
  const [editLocationModalKey, setEditLocationModalKey] = useState(0);

  const [data, setData] = useState(null);

  const {
    loading: locationsListLoading,
    data: locationsList,
    refetch: locationsListRefetch,
  } = useQuery(GET_LOCATIONS_LIST, {
    variables: { isMainLocation: true },
  });

  const columns = [
    {
      title: 'Name in English',
      align: 'center' as const,
      render: (data: any) => {
        return data.node.nameEn;
      },
    },
    {
      title: 'Name in Arabic',
      align: 'center' as const,
      render: (data: any) => {
        return <span className="arabic-field">{data.node.nameAr}</span>;
      },
    },
    {
      title: 'Address',
      align: 'center' as const,
      render: (data: any) => {
        const obj = data.node;
        return obj.mainLocationAddress
          ? obj.governorate && obj.area
            ? obj.governorate + ' | ' + obj.area
            : 'N/A'
          : 'per sub-location';
      },
    },
    {
      title: 'Sub Locations',
      align: 'center' as const,
      render: (data: any) => {
        return data.node.subLocations.length === 0 ? (
          'N/A'
        ) : (
          <UnorderedListOutlined
            onClick={() => {
              setData(data);
              setSubLocationsModal(true);
            }}
          />
        );
      },
    },
    {
      title: (
        <span>
          Is Active&nbsp;&nbsp;
          <InfoCircleOutlined
            title="whether or not to show the location in the application form"
            style={{
              verticalAlign: 'middle',
            }}
          />
        </span>
      ),
      width: '10%',
      align: 'center' as const,
      render: (data: any) => {
        return <Checkbox checked={data.node.isActive} disabled />;
      },
    },
    {
      title: 'Edit',
      align: 'center' as const,
      width: '5%',
      render: (data: any) => {
        return (
          <EditOutlined
            onClick={() => {
              setData(data);
              setEditLocationModal(true);
              setEditLocationModalKey(Date.now());
            }}
          />
        );
      },
    },
  ];

  if (locationsListLoading) return <Loading />;

  return (
    <>
      <Row>
        <Col>
          <Title level={3}>
            Locations List
            <PlusCircleOutlined
              className="mx-3"
              onClick={() => {
                setNewLocationModal(true);
                setNewLocationModalKey(Date.now());
              }}
            />
          </Title>
        </Col>
      </Row>

      <Table
        dataSource={locationsList.locations.edges}
        columns={columns}
        pagination={false}
        rowKey={record => record.node.pk}
      />

      <NewLocationModal
        show={newLocationModal}
        setShow={setNewLocationModal}
        refetchData={locationsListRefetch}
        key={newLocationModalKey}
      />

      {data && (
        <EditLocationModal
          show={editLocationModal}
          setShow={setEditLocationModal}
          data={data}
          refetchData={locationsListRefetch}
          key={editLocationModalKey}
        />
      )}

      {data && (
        <SubLocationsModal
          show={subLocationsModal}
          setShow={setSubLocationsModal}
          data={data}
        />
      )}
    </>
  );
});
